import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { ButtonLink } from '../ButtonLink'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Hero.module.scss'
import { HeroImage } from './HeroImage'

export const Hero: FunctionComponent<Props> = ({
  className,
  description,
  image,
  standfirst,
  title,
  size = 2,
  lazyLoad,
  links,
  ...rest
}) => {
  return (
    <section
      data-testid="hero-outer-wrapper"
      className={classnames(styles.heroOuterWrapper, className)}
      {...rest}
    >
      <TypographyV2
        tag={`h${size}` as const}
        textStyle="sb-text-heading-page-title"
        data-testid="hero-title"
        className={classnames(styles.heroTitle)}
      >
        {title}
      </TypographyV2>
      {standfirst && (
        <TypographyV2
          tag="p"
          textStyle="sb-text-heading-standfirst"
          data-testid="hero-strapline"
          className={classnames(styles.heroStrapline)}
        >
          {standfirst}
        </TypographyV2>
      )}
      {links && links?.length > 0 && (
        <nav>
          <ul className={styles.heroLinkList}>
            {links.map(({ href, text }, index) => (
              <li key={index}>
                <ButtonLink appearance="secondary" href={href}>
                  {text}
                </ButtonLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
      {image && <HeroImage {...image} lazyLoad={lazyLoad} />}
      {description && (
        <TypographyV2
          textStyle="sb-text-body-default-strong"
          className={styles.heroIntro}
        >
          {description}
        </TypographyV2>
      )}
    </section>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Optional class name that can be applied to the hero outer wrapper for extra styling purposes
   */
  className?: string
  /**
   * Title text to be displayed before all other content
   */
  title: string
  /**
   * Optional text to be displayed above the HeroImage
   */
  standfirst?: string
  /**
   * Optional HeroImage component
   */
  image?: Parameters<typeof HeroImage>[0]
  /**
   * Optional description text to be displayed below the HeroImage
   */
  description?: string
  /**
   * Optional size tag for header
   */
  //@TODO https://whichonline.atlassian.net/browse/CTX-906 possibly look to replace this prop and use ValidTypographyElements type
  size?: 1 | 2 | 3 | 4 | 5 | 6
  /**
   * Boolean to control if image should be lazy loaded
   */
  lazyLoad?: boolean
  /**
   * Optional list to be rendered as ButtonLinks
   */
  links?: {
    href: string
    text: string
  }[]
} & ComponentPropsWithoutRef<'section'>
